import { useEffect, useState } from "react";
import { getOptimizedImageUrl } from "@/components/_helpers";
import { dominantTint, tintPalette, type TintPalette } from "@/utils/coverTint";

const SAMPLE_SIZE = 12;

const MAX_ENTRIES = 300;

const palettes = new Map<string, TintPalette | null>();

const pending = new Map<string, Promise<TintPalette | null>>();

function sampleUrl(url: string): string | null {
  const optimized = getOptimizedImageUrl(url, { width: SAMPLE_SIZE, height: SAMPLE_SIZE, fit: "cover", quality: 60 });
  return optimized.startsWith("/") ? optimized : null;
}

function remember(url: string, palette: TintPalette | null) {
  if (palettes.size >= MAX_ENTRIES) {
    const oldest = palettes.keys().next().value;
    if (oldest !== undefined) palettes.delete(oldest);
  }
  palettes.set(url, palette);
}

function loadPalette(url: string): Promise<TintPalette | null> {
  const existing = pending.get(url);
  if (existing) return existing;
  const source = sampleUrl(url);
  if (!source || typeof document === "undefined") return Promise.resolve(null);
  const promise = new Promise<TintPalette | null>((resolve) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const context = canvas.getContext("2d", { willReadFrequently: true });
        if (!context) return resolve(null);
        context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        const color = dominantTint(context.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data);
        resolve(color ? tintPalette(color) : null);
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = source;
  }).then((palette) => {
    remember(url, palette);
    pending.delete(url);
    return palette;
  });
  pending.set(url, promise);
  return promise;
}

export function useCoverTint(url: string | undefined, enabled = true): TintPalette | null {
  const [palette, setPalette] = useState<TintPalette | null>(() => (url ? palettes.get(url) ?? null : null));

  useEffect(() => {
    if (!url) {
      setPalette(null);
      return;
    }
    if (palettes.has(url)) {
      setPalette(palettes.get(url) ?? null);
      return;
    }
    if (!enabled) return;
    let active = true;
    loadPalette(url).then((result) => {
      if (active) setPalette(result);
    });
    return () => {
      active = false;
    };
  }, [url, enabled]);

  return palette;
}

export default useCoverTint;
